import type { MouseEventHandler } from 'react'
import { memo, useCallback, useRef } from 'react'
import clsx from 'clsx'
import { Marker } from 'react-map-gl/mapbox'
import styled from 'styled-components'

import Clickable from 'components/Common/Clickable'
import { TEARDROP_PIN_HEIGHT } from 'tripPlanner/components/TripMaps/MapElement/Pins/MapSelectItemPin'
import SearchCustomPin from 'tripPlanner/components/TripMaps/MapElement/Pins/SearchCustomPin'
import MapInfoWindow from 'tripPlanner/components/TripMaps/MapElement/MapInfoWindow'
import MapSummaryCard from 'tripPlanner/components/TripMaps/SummaryCards'
import type { MarkerItem } from 'tripPlanner/types/tripItem'
import type { GeoPoint } from 'tripPlanner/types/common'

const ACTIVE_MARKER_Z_INDEX = 999
const INACTIVE_MARKER_Z_INDEX = -1

const StyledClickable = styled(Clickable)`
  position: absolute;
  left: 0;
  bottom: 0;
  transform: translateX(-50%);
`

// Mapbox markers do not expose a Google-style OverlayView pane/z-index API, so we
// keep the anchor compensation and stacking on an inner DOM container instead.
const MarkerContent = styled.div`
  position: relative;
  transform: translate(-50%, -100%);

  &.active {
    z-index: ${ACTIVE_MARKER_Z_INDEX};
  }

  &.inactive {
    z-index: ${INACTIVE_MARKER_Z_INDEX};
  }
`

interface Props {
  item: MarkerItem
  position: GeoPoint
  active: boolean
  enableWindow?: boolean
  onSelect: (item: MarkerItem) => void
  onDeselect: () => void
}

function SearchMapMarker({
  active,
  item,
  onDeselect,
  onSelect,
  position,
}: Props) {
  const windowRef = useRef<HTMLDivElement | null>(null)

  const onClick: MouseEventHandler = useCallback(
    (e) => {
      e.preventDefault()
      e.stopPropagation()
      if (active) {
        onDeselect()
      } else {
        onSelect(item)
      }
    },
    [active, item, onDeselect, onSelect],
  )

  return (
    <Marker
      longitude={position.lng}
      latitude={position.lat}
      anchor="bottom"
    >
      <MarkerContent className={clsx({ active, inactive: !active })}>
        <StyledClickable onClick={onClick}>
          <SearchCustomPin isActive={active} />
        </StyledClickable>

        <MapInfoWindow
          ref={windowRef}
          open={active}
          alignment="center"
          position="above"
          style={{ top: -TEARDROP_PIN_HEIGHT * 1.3 }}
        >
          <MapSummaryCard item={item} />
        </MapInfoWindow>
      </MarkerContent>
    </Marker>
  )
}

export default memo(SearchMapMarker)
